import { useState } from "react";
import styled from "@emotion/styled";
import { Box, Fab, Link as MuiLink } from "@mui/material";
import { matchRoutes, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useMandatorContext } from "@rentablo/theming";
import ChatWarning from "@icons/myicons/chat-warning.svg?react";
import { useFeatureFlag } from "@hooks";
import { SupportTicketModal } from "@features/customerSupport";
import {
  BOTTOM_PANEL_INSET_VAR,
  COMPACT_FLOATING_BUTTON_SIZE,
  FAB_BOTTOM,
  Tooltip,
} from "@rentablo/components";

const FabStyled = styled(Fab)`
  position: fixed;
  bottom: calc(var(${BOTTOM_PANEL_INSET_VAR}, 0px) + ${FAB_BOTTOM}px);
  right: ${({ theme }) => theme.spacing(2)};
  z-index: ${({ theme }) => theme.zIndex.appBar};

  ${({ theme }) => theme.breakpoints.down("sm")} {
    width: ${COMPACT_FLOATING_BUTTON_SIZE}px;
    height: ${COMPACT_FLOATING_BUTTON_SIZE}px;
    min-height: 0;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const exceptionRoutes = [
  {
    path: "/web-form/*",
  },
  {
    path: "/import-via-tpp-web-form/*",
  },
  {
    path: "/update-via-tpp-web-form/*",
  },
  {
    path: "/import-bank-connection",
  },
  {
    path: "/update-bank-connection",
  },
  {
    path: "/login",
  },
  {
    path: "/cashback-wizard",
  },
  {
    path: "/cashback-wizard-start",
  },
  {
    path: "/cashback-wizard-summary",
  },
  {
    path: "/cashback-wizard-next-steps",
  },
  {
    path: "/cashback-wizard-next-steps-for-company",
  },
  {
    path: "/new-transaction-wizard",
  },
  {
    path: "/manual-transaction-wizard",
  },
  {
    path: "/edit-transaction-wizard",
  },
  {
    path: "/add-transaction-wizard",
  },
  {
    path: "/investments/:slug/dividend-wizard",
  },
  {
    path: "/goals/new",
  },
  {
    path: "/goals/:goalId/edit",
  },
  {
    path: "/feedback",
  },
];

export const FeedbackButton = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { config, isOnvista } = useMandatorContext();
  const isFeedbackButtonEnabled = useFeatureFlag("FEEDBACK_BUTTON");
  const isSupportFormEnabled = useFeatureFlag("SUPPORT_FORM");
  const [isSupportFormOpen, setIsSupportFormOpen] = useState(false);

  const email = isOnvista
    ? "onvista-cashback@rentablo.de"
    : t("aboutCompany.support.email");

  const match = matchRoutes(exceptionRoutes, location);
  if (match) return null;

  const FabComponent = (
    <FabStyled
      color="primary"
      onClick={
        isSupportFormEnabled ? () => setIsSupportFormOpen(true) : undefined
      }
    >
      <Box display="flex" color="button.primaryButton.textOnDefault">
        <ChatWarning width="30px" height="30px" />
      </Box>
    </FabStyled>
  );

  if (!isFeedbackButtonEnabled) return null;

  if (isSupportFormEnabled) {
    return (
      <>
        <Tooltip
          text={t("customerSupport.tooltip")}
          placement="left"
          isSmall
          arrow
        >
          {FabComponent}
        </Tooltip>
        {isSupportFormOpen && (
          <SupportTicketModal
            isOpen
            onClose={() => setIsSupportFormOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <Tooltip
      text={t("feedbackGlobalButton.tooltip") ?? "Feedback"}
      placement="left"
      isSmall
      arrow
    >
      <MuiLink
        href={`mailto:${email}?subject=${t("feedbackGlobalButton.emailSubject", { teamName: config.name })}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {FabComponent}
      </MuiLink>
    </Tooltip>
  );
};
